import { css } from "dx-styles";
import { useCallback, useEffect, useState } from "react";

import { api } from "../api/client";
import type { PublishDiffResponse, TemplateChange } from "../api/types";
import { Modal, button, errorText, muted, row, stack } from "./primitives";
import { tokens } from "../theme/tokens";

const warning = css({
  border: `1px solid ${tokens.color.signal}`,
  borderRadius: tokens.radius.sm,
  backgroundColor: tokens.color.kraft,
  padding: tokens.space.sm,
});

const warningTitle = css({ fontWeight: 700, color: tokens.color.signal });

const list = css({ margin: 0, paddingInlineStart: tokens.space.md });

const change = css({
  borderTop: `1px solid ${tokens.color.rule}`,
  paddingTop: tokens.space.xs,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const badge = css({
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: "999px",
  padding: `0 ${tokens.space.xs}`,
});

function ruleSentence(template: TemplateChange): string | null {
  const parts: string[] = [];
  if (template.rulesAdded > 0) parts.push(`+${template.rulesAdded} rule${template.rulesAdded === 1 ? "" : "s"}`);
  if (template.rulesRemoved > 0) parts.push(`−${template.rulesRemoved} rule${template.rulesRemoved === 1 ? "" : "s"}`);
  if (template.rulesChanged > 0) parts.push(`${template.rulesChanged} changed`);
  return parts.length === 0 ? null : parts.join(", ");
}

interface Props {
  readonly collectionId: string;
  readonly collectionName: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onPublished: () => void;
}

/**
 * The publish dialog.
 *
 * Publishing is the only action in this app that changes data on other people's
 * phones, so it is the only one that asks — and the asking says what will change,
 * for how many people, in plain words.
 */
export function PublishDialog({ collectionId, collectionName, open, onOpenChange, onPublished }: Props) {
  const [state, setState] = useState<PublishDiffResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setState(null);
    setError(null);
    try {
      setState(await api.publishDiff(collectionId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not work out what would change.");
    }
  }, [collectionId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const publish = useCallback(async () => {
    setPublishing(true);
    setError(null);
    try {
      await api.publish(collectionId);
      onPublished();
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Publishing failed.");
    } finally {
      setPublishing(false);
    }
  }, [collectionId, onOpenChange, onPublished]);

  const changed = (state?.diff.templates ?? []).filter((template) => template.status !== "unchanged");
  const nothingToDo =
    state !== null &&
    !state.diff.isFirstPublish &&
    changed.length === 0 &&
    state.diff.itemsAdded.length === 0 &&
    state.diff.itemsRemoved.length === 0 &&
    state.diff.itemsChanged.length === 0;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={state?.diff.isFirstPublish ? "Publish for the first time?" : "Publish these changes?"}
      description={collectionName}
    >
      <div className={stack}>
        {state === null && error === null ? <span className={muted}>Working out what would change…</span> : null}
        {error ? <span className={errorText}>{error}</span> : null}

        {state ? (
          <>
            {/* The consequence first, in the words that matter. */}
            <div className={warning}>
              <div className={stack}>
                <span className={warningTitle}>
                  {state.subscriberEstimate === 0
                    ? "Nobody is subscribed yet"
                    : state.subscriberEstimate === 1
                      ? "1 subscriber will regenerate their checklists"
                      : `About ${state.subscriberEstimate} subscribers will regenerate their checklists`}
                </span>
                <span>
                  {state.subscriberEstimate === 0
                    ? "This only changes what the next subscriber downloads."
                    : "Their phones will rebuild every trip made from these templates. What they have already packed, their containers, notes and manual quantities are kept."}
                </span>
              </div>
            </div>

            {state.report.hardFailures.length > 0 ? (
              <div className={stack}>
                <span className={errorText}>Publishing is blocked until these are fixed:</span>
                <ul className={list}>
                  {state.report.hardFailures.map((issue) => (
                    <li key={`${issue.code}-${issue.message}`}>{issue.message}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {nothingToDo ? (
              <span className={muted}>
                Nothing has changed since revision {state.publishedRevision}. Publishing again would produce the
                same bytes, so subscribers would not even re-download.
              </span>
            ) : null}

            {changed.length > 0 ? (
              <div className={stack}>
                {changed.map((template) => (
                  <div key={template.templateID} className={change}>
                    <div className={row}>
                      <strong>{template.title}</strong>
                      <span className={badge}>{template.status}</span>
                    </div>
                    {ruleSentence(template) ? <span className={muted}>{ruleSentence(template)}</span> : null}
                    {template.titleChanged ? <span className={muted}>renamed</span> : null}
                    {template.parameterNotes.map((note) => (
                      <span key={note} className={muted}>
                        {note}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            ) : null}

            {state.diff.itemsRemoved.length > 0 ? (
              <div className={stack}>
                <strong>Items subscribers will lose</strong>
                <span className={muted}>{state.diff.itemsRemoved.join(", ")}</span>
              </div>
            ) : null}
            {state.diff.itemsAdded.length > 0 ? (
              <div className={stack}>
                <strong>New items</strong>
                <span className={muted}>{state.diff.itemsAdded.join(", ")}</span>
              </div>
            ) : null}
            {state.diff.itemsChanged.length > 0 ? (
              <div className={stack}>
                <strong>Changed items</strong>
                <span className={muted}>{state.diff.itemsChanged.join(", ")}</span>
              </div>
            ) : null}

            {state.report.warnings.length > 0 ? (
              <details>
                <summary className={muted}>
                  {state.report.warnings.length} warning{state.report.warnings.length === 1 ? "" : "s"} — these do
                  not block publishing
                </summary>
                <ul className={list}>
                  {state.report.warnings.slice(0, 25).map((issue) => (
                    <li key={`${issue.code}-${issue.message}`} className={muted}>
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        ) : null}

        <div className={row}>
          <button
            type="button"
            className={button({ tone: "primary" })}
            // Disabled on a failing gate, with the reasons listed above — never
            // a button that fails when pressed.
            disabled={publishing || state === null || state.report.hardFailures.length > 0}
            onClick={() => void publish()}
          >
            {publishing ? "Publishing…" : state?.diff.isFirstPublish ? "Publish" : "Publish changes"}
          </button>
          <button type="button" className={button()} onClick={() => onOpenChange(false)}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
